import {
  DeleteOutlined,
  DownloadOutlined,
  EyeOutlined,
  LoadingOutlined,
  PlusCircleOutlined,
} from '@ant-design/icons';
import {
  collectAuthoredDocumentNames,
  DEFAULT_MAX_OPTIONAL_DOCUMENTS,
  evaluateConditions,
  foldDocumentName,
} from '@bmo/form-core';
import { Button, Input, message, Tag, Tooltip, Typography, Upload } from 'antd';
import type { RcFile } from 'antd/es/upload/interface';
import { useContext, useEffect, useId, useMemo, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import type { WorkflowStep, WorkflowTemplate } from '@/api/workflows';
import { notifySessionActivity } from '@/auth/sessionTimer';

import { collectGeneratedPdfEntryIds } from './buildESignSubmission';
import { localizedEntryDescription, localizedEntryLabel } from './entryLabel';
import { type FieldValue, RichText } from './FieldRenderer';
import { downloadFileToDevice, openFileInNewTab, previewBlobFor } from './filePreviewActions';
import { formatFileSize } from './formatFileSize';
import { ShowErrorsContext } from './showErrorsContext';
import type { UploadedBucketFile, UploadedEntryFile, UploadedFilesStore } from './uploadedFiles';
import { isScanBlocked } from './uploadedFiles';
import {
  type AdditionalDocumentUploader,
  BUCKET_DOCUMENT_DESCRIPTION_MAX_LENGTH,
  BUCKET_DOCUMENT_NAME_MAX_LENGTH,
  OPTIONAL_BUCKET_DOCUMENT_NAME,
} from './useAdditionalDocumentUpload';
import { type SizeBudget, useSizeBudget } from './useSizeBudget';

const { Text, Paragraph } = Typography;
const { Dragger } = Upload;

// Fallback file-size cap for legacy rows that pre-date the per-entry +
// per-bucket caps. Anything live carries its own `maxFileSizeMb` from the
// BE; this only kicks in if a row somehow arrives without one.
const DEFAULT_MAX_FILE_SIZE_MB = 2;
/**
 * Most "other documents" an advisor can attach when the originator configured no limit.
 *
 * <p>
 * Re-exported from form-core rather than declared here: the number is now a step setting
 * (`step.maxOptionalDocuments`) with a server-side counterpart, and this is only the fallback for
 * a step authored before that setting existed. The name is kept so the tests built around it did
 * not all have to move.
 * </p>
 */
export const MAX_OPTIONAL_DOCUMENTS = DEFAULT_MAX_OPTIONAL_DOCUMENTS;

// AC4 (INFLOWS-201) — the file types the browser can render inline in a new
// tab. Anything not in this set falls through to "download only": the preview
// button is disabled with a tooltip explaining why, and a Download button
// appears beside it so the advisor still has a way to open the file.
//
// `txt` and `csv` are here because a browser renders both perfectly well — but
// only once the blob is typed for them, which is why previews go through
// `previewBlobFor` rather than handing over the picked File directly.
//
// Office formats stay out. They need a viewer library or a server round-trip to
// render, and the converted PDF the delivery pipeline produces (ADR-0029) is not
// reachable from the browser today — there is no read path back for
// additional-file bytes. Download is the honest affordance for them.
const INLINE_PREVIEWABLE_EXTENSIONS = [
  'pdf',
  'jpg',
  'jpeg',
  'png',
  'gif',
  'webp',
  'bmp',
  'svg',
  'txt',
  'csv',
];

function extensionOf(name: string): string {
  const dot = name.lastIndexOf('.');
  return dot >= 0 ? name.slice(dot + 1).toLowerCase() : '';
}

/**
 * Whether this row's description box is closed to further editing.
 *
 * <p>
 * Closed once the file has reached BMO, and not a moment before. The description is the source of
 * §14.1's DOCNAME, but that name is composed SERVER-SIDE at the deliver call — the object in S3
 * keeps the advisor's own filename — so until the drop happens there is nothing minted and nothing
 * to protect. The advisor can upload, re-read, and rewrite the description for as long as they are
 * on the step.
 * </p>
 *
 * <p>
 * It used to close the instant the UPLOAD started, because the name was minted into the object's
 * key at that point. Moving the naming to delivery time is what bought the editable window, and
 * this is where that window ends: after delivery the name is spent, and a Back into the step must
 * show what BMO was actually given rather than something that can still be changed.
 * </p>
 *
 * <p>
 * Preview delivers nothing, so nothing there is ever locked.
 * </p>
 */
function isNameLocked(file: Pick<UploadedBucketFile, 'deliveryState'>): boolean {
  return file.deliveryState === 'delivered';
}

/**
 * The opening characters of `value` that §14.1's DOCNAME field is built from.
 *
 * Cut BEFORE folding, matching the server, which caps the raw `documentName` it receives
 * (`MaxAdvisorDocumentNameLength`) and folds second. Folding first would cap a window BMO never
 * sees. Trimmed either side of the cut so a break at a space leaves no trailing blank.
 */
function documentNameCut(value: string): string {
  return value.trim().slice(0, BUCKET_DOCUMENT_NAME_MAX_LENGTH).trim();
}

/**
 * The DOCNAME the advisor's description yields on its own, or `''` when it yields none.
 *
 * <p>
 * Empty for a blank box, and equally for one whose opening characters do not survive the fold —
 * `???` folds away to nothing, and delivering that as `SUPPORTINGDOC` would throw away the
 * perfectly good name the uploaded file already carries. Both cases mean the same thing to every
 * caller here: this description names nothing, look at the file instead.
 * </p>
 */
export function describedDocumentName(description: string | null | undefined): string {
  const cut = documentNameCut(description ?? '');
  return foldDocumentName(cut) ? cut : '';
}

/**
 * §14.1's DOCNAME for one free-form bucket file: the opening characters of the description the
 * advisor typed beside the dropzone, else the uploaded file's own name without its extension,
 * else the generic constant — each cut to {@link BUCKET_DOCUMENT_NAME_MAX_LENGTH}.
 *
 * <h3>Why the description is the first source</h3>
 * It is the only name on this screen the advisor chose. BMO reads a delivered document by its
 * DOCNAME and by nothing else, so a step's worth of files reaching them as `SCAN0042`, `IMG-1193`
 * and `DOC1` — names a scanner picked — is what this replaces. The full text still travels
 * separately and verbatim as `bmoData.supportingDocs[].description`; only its opening words become
 * the name.
 *
 * <h3>Why the file name is still the fallback</h3>
 * The description is optional and a drop is never refused for a missing one, so the uploaded
 * file's own name remains the next-best thing the advisor did not have to type.
 *
 * The cut happens here, not on the server, so the name sent is the name BMO gets; the server folds
 * and re-caps whatever it receives all the same.
 */
export function bucketDocumentName(
  description: string | null | undefined,
  file: Pick<File, 'name'>,
): string {
  const described = describedDocumentName(description);
  if (described) return described;
  // The last dot separates, matching the server's own `FileXFileName.ExtensionOf` — so a file
  // called ".pdf" has an extension and no name at all, and falls through to the constant rather
  // than being delivered as "PDF".
  const dot = file.name.lastIndexOf('.');
  const baseName = dot >= 0 ? file.name.slice(0, dot) : file.name;
  return documentNameCut(baseName) || OPTIONAL_BUCKET_DOCUMENT_NAME;
}

/**
 * The §14.1 DOCNAME a bucket row is actually delivered under — {@link bucketDocumentName} folded
 * exactly as the server folds it.
 *
 * This, not the typed text, is what uniqueness has to compare: BMO de-duplicates on the folded
 * name, so `Void Cheque` and `void-cheque.pdf` are one document to them.
 *
 * The empty-fold fallback mirrors `WorkflowEndpoints`: a punctuation-only name folds to nothing and
 * the server substitutes `SUPPORTINGDOC` rather than refusing the upload. Two such rows therefore
 * genuinely collide, and comparing the raw `''` would miss it.
 */
export function bucketDeliveryName(
  description: string | null | undefined,
  file: Pick<File, 'name'>,
): string {
  return foldDocumentName(bucketDocumentName(description, file)) || OPTIONAL_BUCKET_DOCUMENT_NAME;
}

/**
 * The DOCNAME a row whose file has NOT landed yet would be delivered under, or `null` when it
 * cannot be known.
 *
 * A description that names nothing falls back to the file's own name, and there is no file — so
 * the honest answer is "unknown", and the inline error stays silent. The drop-time check catches
 * that case instead, which is the first moment the real name exists.
 */
export function pendingDeliveryName(description: string): string | null {
  const described = describedDocumentName(description);
  return described ? foldDocumentName(described) : null;
}

export function canPreviewInBrowser(name: string): boolean {
  return INLINE_PREVIEWABLE_EXTENSIONS.includes(extensionOf(name));
}

/**
 * Where one file stands: uploading, then being scanned for malware, then cleared or refused
 * (ADR-0024).
 *
 * Renders NOTHING in preview, and nothing for a file still sitting in local state — neither has an
 * upload or scan state, so there is no story to tell and the row keeps its original appearance.
 *
 * Upload state takes precedence over scan state because it comes first in the file's life: while
 * bytes are still moving there is no verdict to report yet.
 */
function FileStatusChip({
  file,
}: {
  file: Pick<UploadedEntryFile, 'uploadState' | 'scanState' | 'scanDetail'>;
}) {
  const { t } = useTranslation();
  const key = (suffix: string): string => `formRenderer.additionalDocuments.status.${suffix}`;

  // The spinner is the only moving part on the row while a file is in flight — a static chip on a
  // large upload is indistinguishable from one that has quietly died, and the advisor's next move
  // (wait, or remove and re-pick) depends on telling those apart.
  const inProgress = (
    <Tag icon={<LoadingOutlined />} color="processing">
      {t(key('uploading'))}
    </Tag>
  );

  if (file.uploadState === 'uploading') {
    return inProgress;
  }
  if (file.uploadState === 'failed') {
    return <Tag color="error">{t(key('uploadFailed'))}</Tag>;
  }

  switch (file.scanState) {
    case 'processing':
      // Deliberately reuses the "Uploading" label rather than naming the scan. Telling an uploader
      // that a malware check is running is free reconnaissance for a bad actor probing what gets
      // through; from the advisor's side the file is still on its way, so "Uploading" is honest.
      // The blocker copy below is worded to match — the two must never contradict each other.
      return inProgress;
    case 'ready':
      return <Tag color="success">{t(key('ready'))}</Tag>;
    case 'infected':
      return <Tag color="error">{t(key('infected'))}</Tag>;
    case 'blocked':
      // The raw scanner reason (UNSUPPORTED, PASSWORD_PROTECTED) rides in a tooltip rather than the
      // chip: it is useful to the advisor who wants to know WHY, and meaningless noise to the one
      // who just needs to re-save the file. Never a threat name — the server does not send those.
      return (
        <Tooltip title={file.scanDetail ?? ''}>
          <Tag color="error">{t(key('blocked'))}</Tag>
        </Tooltip>
      );
    case 'timedOut':
      return <Tag color="error">{t(key('timedOut'))}</Tag>;
    default:
      return null;
  }
}

/**
 * One uploaded file, rendered IN THE PLACE ITS DROPZONE OCCUPIED rather than in a list beneath it.
 *
 * <p>
 * It deliberately borrows the dropzone's own dashed border, fill and radius: the file did not move
 * somewhere else, it landed here. That also keeps a slot the same width and roughly the same height
 * whether it is waiting for a file or holding one, so a step full of slots does not reflow much as
 * the advisor fills it in — "much" because the filename WRAPS rather than truncating, so a long one
 * costs the card an extra line. Being able to read the whole name is worth that; the tail is where
 * `…v2` / `…v3` lives, and an ellipsis ate exactly the part that tells two scans apart.
 * </p>
 *
 * <p>
 * The filename itself is the preview affordance for anything the browser can render inline, with
 * the eye button beside it for the same action. A file the browser cannot render (.docx, .xlsx)
 * shows its name as plain text and an unavailable eye — and, beside that, a Download button.
 * </p>
 *
 * <p>
 * <b>Download appears ONLY where preview cannot.</b> It is the way out of a dead end, not a second
 * route to a file the advisor can already see: offering both on every row would put two buttons on
 * every card to serve the rarer half of the formats. Saving the file is also as close to "open this
 * in Word" as a web page gets — there is no API that invokes the OS's app picker, so what the
 * advisor gets is the file on disk and their browser's own download chip one click from opening it.
 * </p>
 *
 * <p>
 * The bytes come from the in-memory `File`, so this costs nothing and works identically in preview
 * and in advisor runtime. The flip side is that it only works for a file picked in THIS session —
 * the store is not rehydrated on resume, which is equally true of preview today.
 * </p>
 */
function UploadedFileCard({
  file,
  onRemove,
}: {
  file: Pick<UploadedEntryFile, 'file' | 'uploadState' | 'scanState' | 'scanDetail'>;
  onRemove: () => void;
}) {
  const { t } = useTranslation();
  const previewable = canPreviewInBrowser(file.file.name);
  const preview = (): void => {
    if (!previewable) return;
    // Retyped rather than passed straight through: a `.txt` or `.csv` whose File carries no type
    // would download instead of render. See `previewBlobFor`.
    openFileInNewTab(previewBlobFor(file.file), t);
  };

  return (
    <div className="bmo-uploaded-file">
      <div className="bmo-uploaded-file__meta">
        {/* A <button>, not an <a>: there is no URL to navigate to — the blob is minted on click —
            and an href-less anchor is not reachable by keyboard. The link styling is CSS only. */}
        {previewable ? (
          <button type="button" className="bmo-uploaded-file__name" onClick={preview}>
            {file.file.name}
          </button>
        ) : (
          <Tooltip title={t('formRenderer.additionalDocuments.previewNotAvailable')}>
            <span className="bmo-uploaded-file__name bmo-uploaded-file__name--plain">
              {file.file.name}
            </span>
          </Tooltip>
        )}
        {/* Size and scan verdict on their own line. The verdict is load-bearing, not decoration:
            Next stays disabled until every file on the step is clean, and this is where the
            advisor sees WHICH file is holding them. */}
        <div className="bmo-uploaded-file__detail">
          {formatFileSize(file.file.size)}
          <FileStatusChip file={file} />
        </div>
      </div>
      {/* Every button here NAMES ITS FILE. A step holds many of these cards, so three buttons per
          card called "Preview" / "Download" / "Remove" put a dozen identically-named controls on
          one page: the rotor lists them as a dozen "Remove"s and a speech-input user saying
          "click Remove" gets an arbitrary one. The visible tooltip word is kept as the LEADING
          word of each name so "click Remove" still matches the control the advisor can see
          (2.5.3 Label in Name) — the filename only disambiguates it. */}
      <div className="bmo-uploaded-file__actions">
        <Tooltip
          title={
            previewable
              ? t('formRenderer.additionalDocuments.preview')
              : t('formRenderer.additionalDocuments.previewNotAvailable')
          }
        >
          {/* aria-disabled rather than `disabled` so keyboard users can still tab onto the button
              and fire the Tooltip — a truly disabled button leaves the tab order and the "preview
              not available" hint becomes unreachable for exactly the people who need it. The
              handler no-ops, so the unavailable state still holds. */}
          <Button
            type="text"
            icon={<EyeOutlined />}
            aria-disabled={!previewable}
            onClick={preview}
            aria-label={
              previewable
                ? t('formRenderer.additionalDocuments.previewFile', { name: file.file.name })
                : t('formRenderer.additionalDocuments.previewNotAvailable')
            }
            style={previewable ? undefined : { opacity: 0.4, cursor: 'not-allowed' }}
          />
        </Tooltip>
        {/* Only where the eye is unavailable — see the component docs. A real <Button> with a real
            click, not an aria-disabled one: this is the row's working affordance, and the whole
            reason it is here is that the button beside it does nothing. */}
        {!previewable && (
          <Tooltip title={t('formRenderer.additionalDocuments.download')}>
            <Button
              type="text"
              icon={<DownloadOutlined />}
              onClick={() => downloadFileToDevice(file.file)}
              aria-label={t('formRenderer.additionalDocuments.downloadFile', {
                name: file.file.name,
              })}
            />
          </Tooltip>
        )}
        <Tooltip title={t('formRenderer.additionalDocuments.remove')}>
          <Button
            type="text"
            icon={<DeleteOutlined />}
            onClick={onRemove}
            aria-label={t('formRenderer.additionalDocuments.removeFile', { name: file.file.name })}
          />
        </Tooltip>
      </div>
    </div>
  );
}

/**
 * Advisor-side runtime UI for an `AdditionalDocuments` step. Renders one
 * upload slot per originator-configured entry (mirrors `PdfGenerationStep`'s
 * one-row-per-entry list), each enforcing its own allowed extensions and
 * showing whether it's required.
 *
 * <p>
 * THE SAME COMPONENT RENDERS IN PREVIEW. The originator's builder preview and the approver's
 * review both mount this step, and neither is a real submission — so uploading is driven by the
 * optional `uploader` prop, which `FormShell` supplies only in advisor runtime. When it is
 * absent, picked files stay in local state exactly as they always have and nothing reaches S3.
 * See `useAdditionalDocumentUpload` for why the guard is an absent function rather than a flag.
 * </p>
 */
export function AdditionalDocumentsStep({
  step,
  template,
  language,
  values,
  uploadedFiles,
  onUploadedFilesChange,
  uploader,
  onSetStepBlocker,
}: {
  step: WorkflowStep;
  /**
   * The whole workflow, not just this step — needed to count the PDFs this application will
   * generate (spec §14.1's `SEQUENCE_IN_PACKAGE` runs across the whole package, PDFs first, so
   * additional documents must start their own numbering after that count). Optional for the same
   * reason `values` is: a preview/test that omits it just gets a zero PDF offset.
   */
  template?: WorkflowTemplate;
  language: 'en' | 'fr';
  /** Current advisor answers — drives entry-level visibility conditions so
   *  "Void Cheque only when Payment = EFT" hides the row when Payment is
   *  something else. Matches the pattern DynamicForm's section conditions
   *  use. Optional so preview/tests that don't own a values map still work
   *  (in that case every entry renders unconditionally). Also drives which
   *  PDF entries count toward the sequence offset above. */
  values?: Record<string, FieldValue>;
  /** FormShell-owned store — survives step navigation and is consumed
   *  by the PdfGeneration step downstream. See `uploadedFiles.ts`. */
  uploadedFiles: UploadedFilesStore;
  onUploadedFilesChange: (
    updater: UploadedFilesStore | ((prev: UploadedFilesStore) => UploadedFilesStore),
  ) => void;
  /** Present ONLY in advisor runtime. Absent in preview — see the component docs. */
  uploader?: AdditionalDocumentUploader;
  /**
   * Disables FormShell's Next with this message, or clears it with `null`. Absent in preview and
   * in tests that render the step standalone, so every call is optional-chained.
   */
  onSetStepBlocker?: (reason: string | null) => void;
}) {
  const { t } = useTranslation();

  /**
   * Pushes one just-picked file to S3 and folds the result back into the store.
   *
   * No-ops when `uploader` is undefined, which is the preview case — so preview keeps the
   * original local-only behaviour and never marks a file as uploading.
   *
   * Errors are caught and recorded on the file rather than thrown: one failed upload must not
   * discard the picked file or take down the step, and the advisor's recovery is to remove and
   * re-pick that row.
   */
  const uploadPicked = async ({
    uid,
    file,
    entryId,
    sequenceInPackage,
    patch,
  }: {
    uid: string;
    file: File;
    /**
     * The originator entry this file fills. Absent for the unnamed optional bucket, which has no
     * entry — and therefore no `doNotParseToPdf` setting, so those always convert.
     *
     * The server resolves the entry from this id rather than trusting a flag from here: the
     * convert decision changes the §14.1 delivery name BMO matches on, so it is not the client's
     * to make.
     */
    entryId?: string;
    /**
     * Review order within the package. Recorded on the row rather than sent now — the name it
     * ends up in is built at the deliver call — so the order the advisor built is the order BMO
     * reviews in.
     */
    sequenceInPackage: number;
    patch: (uid: string, changes: Partial<UploadedEntryFile>) => void;
  }): Promise<void> => {
    if (!uploader) return;
    patch(uid, { uploadState: 'uploading' });
    try {
      const { key, storedFileName } = await uploader({ file, entryId });
      // The bytes are in S3, which is NOT the same as the file being usable: the object lands
      // untagged and the bucket policy denies reads until GuardDuty clears it (ADR-0024). So the
      // file moves straight from uploaded into `scanning`, and its own polling clock starts here —
      // per file, because files are attached minutes apart and one shared window would cut the
      // budget of a late arrival short. `useAdditionalDocumentScanStatus` takes it from here.
      patch(uid, {
        uploadState: 'uploaded',
        s3Key: key,
        storedFileName,
        sequenceInPackage,
        scanState: 'processing',
        scanPollStartedAt: Date.now(),
      });
    } catch (err) {
      console.error('[AdditionalDocumentsStep] upload failed', err);
      patch(uid, {
        uploadState: 'failed',
        uploadError: err instanceof Error ? err.message : String(err),
      });
      void message.error(t('formRenderer.additionalDocuments.uploadFailed', { name: file.name }));
    }
  };

  const setEntryFiles = (entryId: string, files: UploadedEntryFile[]): void => {
    // Counts as session activity: an advisor actively uploading files shouldn't hit the
    // idle-refresh boundary mid-upload. See useSessionTimer.ts.
    notifySessionActivity();
    onUploadedFilesChange((prev) => ({
      ...prev,
      entries: { ...prev.entries, [entryId]: files },
    }));
  };
  // Takes an updater as well as a list: a multi-file drop commits several files before the step
  // re-renders, and each has to land on top of the one before it rather than on the list as it
  // stood when the drop began.
  const setBucket = (
    next: UploadedBucketFile[] | ((prev: UploadedBucketFile[]) => UploadedBucketFile[]),
  ): void => {
    notifySessionActivity();
    onUploadedFilesChange((prev) => ({
      ...prev,
      bucket: typeof next === 'function' ? next(prev.bucket) : next,
    }));
  };

  // Patches merge into whatever the store holds NOW rather than into a captured copy: an upload
  // resolves after the picked file was already committed, and the advisor may have added or
  // removed rows in between. A file removed mid-flight simply isn't found and the patch is a
  // no-op, which is the correct outcome.
  const patchEntryFile = (entryId: string) => (uid: string, changes: Partial<UploadedEntryFile>) =>
    onUploadedFilesChange((prev) => ({
      ...prev,
      entries: {
        ...prev.entries,
        [entryId]: (prev.entries[entryId] ?? []).map((f) =>
          f.uid === uid ? { ...f, ...changes } : f,
        ),
      },
    }));

  const patchBucketFile = (uid: string, changes: Partial<UploadedEntryFile>) =>
    onUploadedFilesChange((prev) => ({
      ...prev,
      bucket: prev.bucket.map((f) => (f.uid === uid ? { ...f, ...changes } : f)),
    }));

  // Entry-level visibility conditions — mirror the DynamicForm section
  // pattern so an entry with unmet conditions doesn't render (and doesn't
  // count against Next-gating either; see the matching branch in
  // FormShell's `isStepComplete` / `missingRequiredFields`).
  const allEntries = step.additionalDocumentEntries ?? [];
  const entries = allEntries.filter((entry) => {
    if ((entry.conditions?.length ?? 0) === 0) return true;
    return evaluateConditions(entry.conditions, entry.conditionCombinator, values ?? {});
  });
  // Additional documents are numbered AFTER the generated PDFs in the OneSpan package's one
  // running SEQUENCE_IN_PACKAGE order (spec §14.1). "Would generate" uses the same
  // evaluateConditions resolution ESignSubmission.generatedPdfEntryIds uses at submit — the
  // backend never sees form answers before then, so this can only be resolved here.
  const pdfOffset = template ? collectGeneratedPdfEntryIds(template, values ?? {}).length : 0;

  /**
   * Every §14.1 DOCNAME the ORIGINATOR already reserved on this workflow — generated PDFs and named
   * document slots alike. A name the advisor types into the optional bucket must miss all of them.
   *
   * <p>
   * Deliberately NOT condition-filtered, unlike `entries` above. The advisor can change an answer
   * after uploading, and `isNameLocked` freezes a bucket name the instant its upload starts — so an
   * entry that was hidden at upload time and comes back into scope would collide with a name that
   * can no longer be edited, recoverable only by removing and re-uploading the file. Reserving a
   * few names the advisor never sees costs nothing next to that.
   * </p>
   */
  const reservedDocumentNames = useMemo(
    () => new Set(collectAuthoredDocumentNames(template?.steps ?? [step]).keys()),
    [template, step],
  );

  // The combined cap across the mandatory section, weighed at pick time.
  //
  // It lives HERE rather than inside EntrySlot because it spans every required entry at once —
  // no single slot can know what the others hold. Only `isRequired` entries are charged: the cap
  // is the one quoted under the "Mandatory Documents" heading, and weighing the optional rows
  // against it would be the same broken promise the heading itself was once removed for. A
  // named-but-optional entry is not left unguarded either — it keeps its own per-file cap.
  //
  // Built from `entries`, which is already condition-filtered, so a required entry the advisor
  // cannot see contributes nothing to a limit they cannot act on.
  const mandatoryBudget = useSizeBudget(
    step.mandatoryCombinedMaxSizeMb,
    entries
      .filter((entry) => entry.isRequired)
      .map((entry) => ({
        key: entry.id,
        bytes: (uploadedFiles.entries[entry.id] ?? []).reduce((sum, f) => sum + f.file.size, 0),
      })),
  );

  const bucketExtensions = step.optionalDocumentExtensions ?? [];
  // One cap for the whole bucket — read once so the heading's note and the dropzone's own
  // validation can never quote different numbers.
  const bucketMaxFileSizeMb = step.optionalDocumentMaxFileSizeMb ?? DEFAULT_MAX_FILE_SIZE_MB;
  // Advisor/preview visibility toggle set by the originator — same component
  // renders both preview and the live advisor form, so this one guard covers
  // both. See AdditionalDocumentEntriesDrawer's "Other Additional Documents"
  // Switch.
  const bucketVisible = bucketExtensions.length > 0 && step.showOptionalDocumentsSection;
  const description = language === 'fr' ? step.descriptionFr : step.descriptionEn;

  // Files this STEP is responsible for. Scoped to the entries actually rendered plus the bucket,
  // rather than the whole store: with two AdditionalDocuments steps in a workflow, a refused file
  // on the other one would otherwise disable Next here with a message naming a file the advisor
  // cannot see from this page.
  const stepFiles: UploadedEntryFile[] = [
    ...entries.flatMap((entry) => uploadedFiles.entries[entry.id] ?? []),
    ...(bucketVisible ? uploadedFiles.bucket : []),
  ];

  // Blocks Next until every file on this step is confirmed safe (ADR-0024, R-24).
  //
  // Fails closed by construction: a file only stops blocking when it reaches `clean`. Uploading,
  // scanning, refused and timed-out all hold the advisor here, and so does any state we have not
  // thought of, because nothing but `clean` clears the check.
  //
  // Inert in PREVIEW without needing a mode flag — preview never uploads, so its files carry
  // neither an upload state nor a scan state and every branch below is false.
  //
  // `stepFiles` is rebuilt each render, so the message is memoized on a signature of just the
  // fields that can change it. Without that the effect would re-run on every keystroke elsewhere
  // in the form.
  const blockerSignature = stepFiles
    .map((f) => `${f.uid}:${f.uploadState ?? ''}:${f.scanState ?? ''}`)
    .join('|');
  const blockerReason = useMemo(() => {
    const firstUploadFailure = stepFiles.find((f) => f.uploadState === 'failed');
    const firstNotCleared = stepFiles.find((f) => isScanBlocked(f));

    // Order matters: report the earliest stage that is still unresolved, so the advisor is told
    // about the upload that failed rather than about the scan that consequently never started.
    if (stepFiles.some((f) => f.uploadState === 'uploading')) {
      return t('formRenderer.additionalDocuments.blockUploading');
    }
    if (firstUploadFailure) {
      return t('formRenderer.additionalDocuments.blockUploadFailed', {
        name: firstUploadFailure.file.name,
      });
    }
    // One refusal, three different truths (QA AC10). For a THREATS_FOUND verdict the server
    // DELETES the object — `ScanEventProcessor` removes it, and any PDF converted from it, from an
    // unversioned bucket — so "not saved" is literally accurate and the advisor's next move is to
    // find a different document. `blocked` (unsupported / password-protected) and `timedOut` leave
    // the object exactly where it is; telling the advisor those weren't saved would be false, and
    // their next move is to re-save or re-upload the SAME file.
    //
    // This is the VERDICT, not the scan in progress. While a scan is running the UI still says
    // "Uploading" and still names nothing (see FileStatusChip and the `processing` branch below) —
    // the obscurity rule is about concealing that a check is happening at all, and once there is a
    // verdict there is nothing left to conceal.
    if (firstNotCleared) {
      // Falls back rather than switching exhaustively: TERMINAL_BAD_SCAN_STATES can grow, and a
      // state we have not written copy for must still produce a blocker rather than return null
      // and quietly let the advisor through.
      const key =
        firstNotCleared.scanState === 'infected'
          ? 'blockInfected'
          : firstNotCleared.scanState === 'blocked'
            ? 'blockFileBlocked'
            : firstNotCleared.scanState === 'timedOut'
              ? 'blockTimedOut'
              : 'blockNotCleared';
      return t(`formRenderer.additionalDocuments.${key}`, {
        name: firstNotCleared.file.name,
      });
    }
    // Same message as a file whose bytes are still moving: the row chip says "Uploading" while the
    // scan runs, and a banner that named the virus check would give the game away. See FileStatusChip.
    if (stepFiles.some((f) => f.scanState === 'processing')) {
      return t('formRenderer.additionalDocuments.blockUploading');
    }
    return null;
    // stepFiles is intentionally read through the signature above rather than listed directly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blockerSignature, t]);

  useEffect(() => {
    onSetStepBlocker?.(blockerReason);
  }, [onSetStepBlocker, blockerReason]);

  // Leaving the step must not strand its blocker on the shell — FormShell keys StepContent by step
  // id, so this unmounts on navigation and the next step starts unblocked. Matches the equivalent
  // cleanup in OneSpanESignStep.
  useEffect(() => () => onSetStepBlocker?.(null), [onSetStepBlocker]);

  if (entries.length === 0 && !bucketVisible) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        {description && (
          <Paragraph style={{ marginBottom: 0, color: '#111827', fontSize: 14, fontWeight: 600 }}>
            {description}
          </Paragraph>
        )}
        <Text type="secondary" style={{ fontSize: 13 }}>
          {t('formRenderer.additionalDocuments.empty')}
        </Text>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
      <div>
        {/* The step title renders only in the left rail, so the content area
            needs its own page heading — same rule as the DynamicForm steps. */}
        <h2 style={{ fontSize: 24, fontWeight: 700, margin: '0 0 8px 0' }}>
          {(language === 'fr' ? step.titleFr : step.titleEn) ||
            t('formRenderer.additionalDocuments.pageTitle')}
        </h2>
        {/* The reminder sits directly under the title, which is where the design puts it. The
            originator's own step description wins when they wrote one — two introductions stacked
            on top of each other read as an argument about what the page is for. */}
        <Paragraph style={{ marginBottom: 0, color: '#475569', fontSize: 14 }}>
          {description || t('formRenderer.additionalDocuments.pageInfo')}
        </Paragraph>
      </div>

      {/* The "Mandatory Documents" heading is back over the named entries.

          It was removed because entries default to optional, so it routinely sat above a list with
          no required asterisk in it — a promise about the rows beneath that they did not keep. The
          design answers that objection rather than ignoring it: the list stays whole and the red
          asterisk marks which rows are genuinely required, so the heading names the section while
          the rows state their own obligation. Keeping ONE list also leaves `sequenceInPackage`
          exactly as it was; partitioning would have reordered what BMO reviews.

          The size line beneath is a cap the step now actually enforces, not the largest per-entry
          number dressed up as a total — which is the other half of why the old line was wrong. */}
      {entries.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <GroupHeading
            title={t('formRenderer.additionalDocuments.mandatoryDocsHeading')}
            capMb={mandatoryBudget.capMb}
          />
          {entries.map((entry, index) => (
            <EntrySlot
              key={entry.id}
              // Advisor UI text, so the UI language. The DOCNAME below stays English.
              label={localizedEntryLabel(entry, language)}
              // Likewise advisor UI text, and likewise never BMO-facing — see
              // `localizedEntryDescription`.
              description={localizedEntryDescription(entry, language)}
              isRequired={entry.isRequired}
              allowedExtensions={entry.allowedExtensions}
              maxFileSizeMb={entry.maxFileSizeMb ?? DEFAULT_MAX_FILE_SIZE_MB}
              // Only a required entry is charged to the mandatory budget — see its definition.
              budget={entry.isRequired ? mandatoryBudget : undefined}
              budgetKey={entry.id}
              files={uploadedFiles.entries[entry.id] ?? []}
              onFilesChange={(files) => setEntryFiles(entry.id, files)}
              // Sequence is the entry's position among the VISIBLE entries, 1-based, offset past
              // the generated PDFs (pdfOffset) — it is the review order a human sees in the
              // OneSpan package, so it follows what the advisor was actually shown rather than
              // the unfiltered template order.
              onFileAdded={(uid, file) =>
                void uploadPicked({
                  uid,
                  file,
                  // The server reads this entry's own label as §14.1's DOCNAME at the deliver
                  // call — ALWAYS the English one, whatever language the advisor works in, since
                  // BMO identifies the document by it and REPLACE/NEW matches on it (QA D-31).
                  // Sending the id rather than the label is what keeps that true.
                  entryId: entry.id,
                  sequenceInPackage: pdfOffset + index + 1,
                  patch: patchEntryFile(entry.id),
                })
              }
            />
          ))}
        </div>
      )}

      {bucketVisible && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <GroupHeading
            title={t('formRenderer.additionalDocuments.optionalDocsHeading')}
            capMb={step.optionalCombinedMaxSizeMb ?? null}
            // Every file in the bucket is held to the SAME formats and the same per-file cap, so
            // unlike the named entries it can state them once for the section. `bucketVisible`
            // already guarantees at least one extension, so there is no any-format case here.
            formatsNote={t('formRenderer.additionalDocuments.formatsOnlyWithSize', {
              list: formatNames(bucketExtensions, t('formRenderer.additionalDocuments.formatsOr')),
              max: bucketMaxFileSizeMb,
            })}
          />
          <OptionalDocumentsBucket
            allowedExtensions={bucketExtensions}
            maxFileSizeMb={bucketMaxFileSizeMb}
            combinedMaxSizeMb={step.optionalCombinedMaxSizeMb}
            maxDocuments={step.maxOptionalDocuments ?? DEFAULT_MAX_OPTIONAL_DOCUMENTS}
            files={uploadedFiles.bucket}
            reservedDocumentNames={reservedDocumentNames}
            onFilesChange={setBucket}
            // Bucket files have no entry label, so §14.1's DOCNAME is the opening words of the
            // description the advisor typed beside the dropzone, falling back to the uploaded
            // file's own name (see bucketDocumentName). Their sequence continues after the named
            // entries (and, like those, after the generated PDFs via pdfOffset) so the package
            // ordering stays stable regardless of how many optional files are attached.
            onFileAdded={(uid, file, indexInBucket) =>
              void uploadPicked({
                uid,
                file,
                // No entryId: the bucket has no originator entry, so there is no
                // do-not-convert setting to honour and these always convert — and no label, so
                // the DOCNAME comes from the advisor's description at the deliver call.
                sequenceInPackage: pdfOffset + entries.length + indexInBucket + 1,
                patch: patchBucketFile,
              })
            }
          />
        </div>
      )}
    </div>
  );
}

/**
 * A section heading and the combined-size line beneath it.
 *
 * <p>
 * The line now quotes a COMBINED cap, and only when the originator configured one. That wording
 * was deliberately removed once before, because it described a total nothing enforced — every
 * upload was only ever weighed against its own per-file cap. It is back because the total is real
 * now: `useSizeBudget` refuses the file that would push the section over it.
 * </p>
 *
 * <p>
 * A null cap renders no line at all rather than a line quoting a default. A step the originator
 * never configured genuinely has no combined limit, and inventing a number to display would
 * promise a rule the step does not apply.
 * </p>
 */
function GroupHeading({
  title,
  capMb,
  formatsNote,
}: {
  title: string;
  /** Null when no combined cap is configured — the line is then omitted entirely. */
  capMb: number | null;
  /**
   * Accepted formats and the per-file cap for a section whose files share ONE set of both — the
   * optional bucket. The named entries each state their own beside the row, so the mandatory
   * heading passes nothing here: a group line there could only ever quote one of several caps,
   * which is the over-claiming that got the old size line removed.
   */
  formatsNote?: string;
}) {
  return (
    <div>
      <h2 style={{ fontSize: 20, fontWeight: 700, margin: '0 0 6px 0' }}>{title}</h2>
      {capMb !== null && (
        <Text style={{ fontSize: 14, color: '#475569' }}>
          {/* The figure is bolded by <strong> markup INSIDE the translated sentence, not by
              splitting the sentence in code. The split is what hard-coded an English unit onto
              French forms before: the UNIT differs by language ("MB" / "Mo"), so the number and
              its unit have to stay inside the string each translator owns. */}
          <Trans
            i18nKey="formRenderer.additionalDocuments.combinedMaxSize"
            values={{ max: capMb }}
            components={{ strong: <strong /> }}
          />
        </Text>
      )}
      {formatsNote !== undefined && (
        /* Same sentence, same 12px grey as the note under a named entry — the two sections state
           what they accept in one voice. It sits UNDER the combined-size line because the
           combined cap governs the section and this governs each file in it. */
        <div style={{ marginTop: 2, fontSize: 12, color: '#6b7280' }}>{formatsNote}</div>
      )}
    </div>
  );
}

/**
 * The dashed drop target. One line — "Browse files or drag and drop from your
 * device" — with only "Browse files" in BMO blue, per the client's Figma. The
 * old treatment stacked a cloud glyph, a prompt and an accepted-formats line;
 * formats now sit beside the label on the left instead.
 *
 * <h3>How this is named, and why through its CHILDREN</h3>
 * rc-upload renders the dashed area as a `role="button"` span, and the real `<input type="file">`
 * inside it is `display:none` — so the button is what a screen reader lands on and the input is
 * not. Its accessible name comes from its CONTENTS, and the contents are the same sentence on
 * every slot: a step with five documents offered five buttons all called "Browse files or drag
 * and drop from your device", with nothing to say which document each one filled. The label
 * naming the slot sits in the OTHER grid column, which is visual adjacency and nothing more.
 *
 * `aria-label` on the Dragger does NOT fix that, which is worth stating because it looks like it
 * should: antd spreads unknown props into rc-upload, but rc-upload applies
 * `pickAttrs(..., {aria: true})` to the hidden INPUT, not to the wrapper that carries the role.
 * The attribute lands on an element nothing can reach and the button keeps its generic name.
 *
 * So the qualifier goes where the name actually comes from — a visually hidden child. The visible
 * sentence stays first and stays whole, so the accessible name still CONTAINS the visible label
 * and speech input keeps working (2.5.3); `slotName` only adds what the eye already gets from the
 * column beside it.
 */
function DropZone({
  beforeUpload,
  acceptedAttr,
  multiple = true,
  slotNote,
  t,
}: {
  beforeUpload: (file: RcFile) => boolean | string;
  acceptedAttr: string | undefined;
  /** False for a named entry, which holds exactly one file. */
  multiple?: boolean;
  /**
   * What this target is FOR — "for Void Cheque", or "for Document 3" on a bucket row with no name
   * yet, and the variant naming the outstanding requirement when the slot is being complained
   * about. Required rather than optional so a new slot cannot quietly go back to being the fifth
   * anonymous button.
   *
   * <p>
   * A PHRASE, never the bare label, and one span rather than two. Hidden text that reads exactly
   * the same as visible text makes both ambiguous to anything matching on text — `getByText`
   * here, but equally a person scanning the DOM — and one of this step's own tests asserts
   * "Upload required." appears exactly once, which a verbatim hidden copy would quietly defeat.
   * Phrasing it differently keeps the announcement and leaves every text match unambiguous.
   * </p>
   *
   * <p>
   * The requirement rides in the NAME rather than in `aria-describedby` because there is no
   * exposed element to point a description at (see above). The visible `role="alert"` still
   * announces the moment it appears; this is what an advisor hears when they tab back to the
   * control later, which is exactly when that live announcement is long gone.
   * </p>
   */
  slotNote: string;
  t: (key: string, opts?: Record<string, unknown>) => string;
}) {
  return (
    <Dragger
      multiple={multiple}
      showUploadList={false}
      beforeUpload={beforeUpload}
      accept={acceptedAttr}
      className="bmo-dropzone"
    >
      <span style={{ fontSize: 14, color: '#0179C1', fontWeight: 700 }}>
        {t('formRenderer.additionalDocuments.browseFiles')}
      </span>{' '}
      <span style={{ fontSize: 14, color: '#111827' }}>
        {t('formRenderer.additionalDocuments.orDragDrop')}
      </span>
      {/* `sr-only`, not `aria-hidden` on the rest: the whole visible sentence has to stay in the
          name. This only appends. */}
      <span className="sr-only"> {slotNote}</span>
    </Dragger>
  );
}

/** Extensions that name the SAME format — collapsed for display so a slot
 *  accepting .jpg and .jpeg reads "JPEG", not "JPG, JPEG". Validation still
 *  uses the raw extension list; this is presentation only. */
const FORMAT_DISPLAY_NAME: Record<string, string> = { jpg: 'JPEG', jpeg: 'JPEG' };

/** Extension → MIME type for the picker's `accept` attribute. Mirrors the back end's
 *  `WorkflowStep.SupportedExtensions`; an extension missing here simply contributes no
 *  MIME token, so the list can lag without breaking the picker. */
const EXTENSION_MIME_TYPE: Record<string, string> = {
  pdf: 'application/pdf',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  csv: 'text/csv',
  txt: 'text/plain',
};

/**
 * Builds the `accept` attribute: every allowed extension, PLUS the MIME type each one names.
 *
 * <h3>Why both, when extensions alone already filtered the picker</h3>
 * iOS is the reason. An iPhone stores photos as HEIC, which nothing in this pipeline can convert
 * — ADR-0025 leaves HEIC explicitly uncovered and the conversion router fails closed on it. When
 * Safari's photo picker sees an `accept` list that names JPEG as a *MIME type* and does not name
 * HEIC, it transcodes the chosen photo to JPEG on the way out, so the advisor uploads something
 * the converter can actually read. An extension-only list (`.jpg,.png`) is a much weaker signal
 * and does not reliably trigger that.
 *
 * Extensions stay in the list because desktop browsers and the iOS *Files* picker match on them,
 * and dropping them would narrow what those pickers show. Browsers union the two kinds of token,
 * so listing both only ever widens the picker.
 *
 * <h3>This is a hint, never a guard</h3>
 * `accept` is advisory — a drag-and-drop or a renamed file sails straight past it. The real
 * boundary is the extension check in each slot's `beforeUpload`, backed by the server's own
 * re-check in `WorkflowEndpoints`. Neither of those changes here.
 */
function acceptAttr(allowedExtensions: string[]): string | undefined {
  if (allowedExtensions.length === 0) return undefined;

  const extensions = allowedExtensions.map((e) => `.${e.toLowerCase()}`);
  // Deduped: jpg and jpeg both name image/jpeg, and a repeated token reads like a bug in devtools.
  const mimeTypes = Array.from(
    new Set(
      allowedExtensions
        .map((e) => EXTENSION_MIME_TYPE[e.toLowerCase()])
        .filter((m): m is string => m !== undefined),
    ),
  );

  return [...extensions, ...mimeTypes].join(',');
}

/** "JPEG or PDF" / "JPEG, PNG or PDF" — the design joins with "or", not commas. */
export function formatNames(allowedExtensions: string[], or: string): string {
  const names = Array.from(
    new Set(allowedExtensions.map((e) => FORMAT_DISPLAY_NAME[e.toLowerCase()] ?? e.toUpperCase())),
  );
  if (names.length <= 1) return names[0] ?? '';
  // The joining word is translated — "or" in English, "ou" in French (QA D-31).
  return `${names.slice(0, -1).join(', ')} ${or} ${names[names.length - 1]}`;
}

/** One originator-configured document slot — its own dropzone, validation, and file list. */
function EntrySlot({
  label,
  description,
  isRequired,
  allowedExtensions,
  maxFileSizeMb,
  budget,
  budgetKey,
  files,
  onFilesChange,
  onFileAdded,
}: {
  label: string;
  /**
   * Originator-authored guidance for this slot, already resolved to the advisor's language.
   * Empty string = none authored, and nothing is rendered at all — the slot then looks exactly
   * as it did before the field existed.
   */
  description: string;
  isRequired: boolean;
  allowedExtensions: string[];
  maxFileSizeMb: number;
  /** The step's mandatory combined budget — present only on a required entry. */
  budget?: SizeBudget;
  /** This slot's key in that budget's ledger. The entry id, so a replaced file releases its
   *  predecessor's bytes rather than stacking on top of them. */
  budgetKey: string;
  files: UploadedEntryFile[];
  onFilesChange: (files: UploadedEntryFile[]) => void;
  /** Fired after a file passes validation and is committed locally. */
  onFileAdded: (uid: string, file: File) => void;
}) {
  const { t } = useTranslation();
  // A required slot still empty once Next has been pressed says so under itself (QA ESU-015:
  // "Upload required."), not only in the toast naming the slot.
  //
  // An entry its conditions were hiding is not mounted, so the Next that raised the signal never
  // asked it for a file. Same born-tick rule the form fields follow (see FieldRenderer): answer
  // only to a tick that moves PAST the one this slot appeared under, so a slot revealed by a
  // later answer starts clean and is named by the following Next.
  const raisedTick = useContext(ShowErrorsContext);
  const [bornTick] = useState(raisedTick);
  const missing = isRequired && files.length === 0 && raisedTick > bornTick;

  const acceptedAttr = acceptAttr(allowedExtensions);
  const allowedListText = allowedExtensions.map((e) => e.toUpperCase()).join(', ');
  const maxBytes = maxFileSizeMb * 1024 * 1024;

  // AntD's beforeUpload accepts `boolean | string | Promise<...>`. We return
  // either `false` (track locally, don't auto-POST) OR the `LIST_IGNORE`
  // sentinel string (reject + don't put on AntD's internal list). The literal
  // string `Upload.LIST_IGNORE` is the contract; never cast it to `false`,
  // wrappers branching on the truthy/falsy of the return would mis-handle a
  // truthy "__LIST_IGNORE__" string.
  const beforeUpload = (file: RcFile): boolean | string => {
    if (allowedExtensions.length > 0) {
      const dot = file.name.lastIndexOf('.');
      const ext = dot >= 0 ? file.name.slice(dot + 1).toLowerCase() : '';
      if (!allowedExtensions.includes(ext)) {
        void message.error(
          t('formRenderer.additionalDocuments.rejectedExtension', {
            name: file.name,
            allowed: allowedListText,
          }),
        );
        return Upload.LIST_IGNORE;
      }
    }
    if (file.size > maxBytes) {
      void message.error(
        t('formRenderer.additionalDocuments.rejectedSize', {
          name: file.name,
          max: maxFileSizeMb,
        }),
      );
      return Upload.LIST_IGNORE;
    }
    // The combined cap across the whole mandatory section, after the per-file cap and LAST of the
    // gates — `tryReserve` mutates the ledger, so nothing may refuse the file once its bytes have
    // been charged. Checked at pick time, like every other refusal here, so a file over the limit
    // never reaches S3, never gets scanned, and never has to be un-delivered.
    if (budget && !budget.tryReserve(budgetKey, file.size)) {
      void message.error(
        t('formRenderer.additionalDocuments.rejectedCombinedSize', {
          name: file.name,
          max: budget.capMb,
        }),
      );
      return Upload.LIST_IGNORE;
    }
    // Track locally so PdfGenerationStep can read the actual File later.
    // Append a monotonic suffix so re-picking the same file (identical
    // name + size + lastModified after a remove + re-add) still produces
    // a unique React key.
    const uid = `${file.name}-${file.size}-${file.lastModified}-${files.length}`;
    // ONE file per named entry: a new pick REPLACES what is there (QA D-44 / ESU-011). Not a UI
    // preference — every file in an entry is delivered under the same §14.1 DOCNAME, obligation
    // and sequence, so BMO receives a second file as a REPLACE of the first anyway (see
    // AdditionalDocumentDisposition). Showing two files here promised something BMO never keeps.
    onFilesChange([{ uid, file }]);
    // Commit locally FIRST, then upload. The row appears immediately and picks up its
    // uploading/uploaded state as the request progresses; a failed upload leaves the file in
    // place to retry rather than making it vanish. In preview this is a no-op.
    onFileAdded(uid, file);
    return false;
  };

  const removeFile = (uid: string) => onFilesChange(files.filter((f) => f.uid !== uid));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {/* Label + accepted formats on the left, drop target on the right —
          the Figma's two-column slot. The old "Required" / "Optional" tags
          are gone: a red asterisk carries the same meaning and matches every
          other required field in the form. */}
      <div className="bmo-doc-slot">
        <div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
            <span className="bmo-doc-slot__bullet" aria-hidden="true" />
            <Text strong style={{ fontSize: 14, color: '#111827' }}>
              {label}
              {isRequired && (
                <span aria-hidden="true" style={{ color: '#dc2626', marginLeft: 4 }}>
                  *
                </span>
              )}
            </Text>
          </div>
          {/* The originator's own guidance for this document, between the title it belongs to and
              the formats line — the order the design asks for, and the order it reads in: what
              this document is, then what the slot will accept.

              Through RichText, so `[label](https://…)` reaches the advisor as a real link (new
              tab, rel="noopener") rather than as literal markdown. That is the whole reason it is
              not a plain string here: a document slot's guidance is routinely "see the sample",
              and the project renders every other piece of authored prose the same way.

              Sized BETWEEN the title and the formats line (13px / #475569 against 14px bold and
              12px grey) so the three read as one descending hierarchy rather than as two captions
              competing under a heading. Nothing renders at all when none is authored — see the
              prop's docs. */}
          {description && (
            <RichText
              body={description}
              // The trailing margin lives HERE rather than on the formats line below, so a slot
              // with no description keeps the exact spacing it has always had.
              style={{
                marginLeft: 16,
                marginTop: 4,
                marginBottom: 6,
                fontSize: 13,
                color: '#475569',
              }}
            />
          )}
          <div style={{ marginLeft: 16, marginTop: 2, fontSize: 12, color: '#6b7280' }}>
            {/* Accepted formats AND this slot's own size cap. The cap is per entry, so it belongs
                on the entry rather than in a group line quoting one figure for rows that each
                enforce a different one. */}
            {allowedExtensions.length > 0
              ? t('formRenderer.additionalDocuments.formatsOnlyWithSize', {
                  list: formatNames(
                    allowedExtensions,
                    t('formRenderer.additionalDocuments.formatsOr'),
                  ),
                  max: maxFileSizeMb,
                })
              : t('formRenderer.additionalDocuments.anyFormatWithSize', { max: maxFileSizeMb })}
          </div>
        </div>
        {/* The file TAKES THE DROPZONE'S PLACE rather than stacking under it. A named entry holds
            exactly one file (a fresh pick replaces it, QA D-44 / ESU-011), so once one is here
            there is nothing left to drop — and an empty target sitting above a filled slot read as
            an invitation to add a second file that the slot would silently swallow. Removing the
            file brings the dropzone back, which is the way to swap one.

            Mapped rather than indexed at [0]: the one-file rule is enforced in `beforeUpload`, and
            a store that somehow holds two should show both rather than hide one behind the other. */}
        {files.length > 0 ? (
          <div style={{ display: 'grid', gap: 8 }}>
            {files.map((f) => (
              <UploadedFileCard key={f.uid} file={f} onRemove={() => removeFile(f.uid)} />
            ))}
          </div>
        ) : (
          <DropZone
            beforeUpload={beforeUpload}
            acceptedAttr={acceptedAttr}
            multiple={false}
            // Names the DOCUMENT this target fills. The label that says so is in the left grid
            // column and never reached the button's own name — see DropZone's docs.
            slotNote={
              missing
                ? t('formRenderer.additionalDocuments.dropzoneForRequired', { name: label })
                : t('formRenderer.additionalDocuments.dropzoneFor', { name: label })
            }
            t={t}
          />
        )}
      </div>
      {missing && (
        /* `role="alert"` ANNOUNCES it the moment it appears. Attaching it to the control is the
           other half, and that cannot be done from here — the drop target exposes no element to
           point `aria-describedby` at, so the same text is repeated into its name instead. See
           DropZone's `requiredNote`. */
        <div role="alert" style={{ fontSize: 12, color: '#dc2626', marginLeft: 16 }}>
          {t('formRenderer.additionalDocuments.uploadRequired')}
        </div>
      )}
    </div>
  );
}

/**
 * A box as tall as the dashed drop target across the row, taken from the one height
 * `.bmo-doc-slot` declares for both of its halves. Fixed rather than autoSizing from a
 * single line: the pair has to read as one row, and a box that grew as the advisor typed
 * left the two halves level only while it was empty. Overflow scrolls; the drag handle is
 * off, since resizing it would break the same pairing.
 *
 * Applied through `styles.textarea`, NOT `style` — with `showCount` set, antd hands `style`
 * to the wrapper around the box and forwards only `resize` from it to the box itself.
 */
const DESCRIPTION_TEXTAREA_STYLE = {
  height: 'var(--bmo-doc-slot-row-h)',
  resize: 'none',
} as const;

/**
 * Width of a pending row's delete button. Stood in for the button on the rows that do not carry
 * one, so every dropzone in the list ends at the same x.
 *
 * <p>
 * It is NOT AntD's default square, which is what this said while the theme sized every Button at
 * 56px (theme.ts `Button.controlHeight`) — the spacer was 24px narrower than the thing it stood
 * in for, so the rows it padded ended short of the ones that carry a real button. 32px is now
 * true because `.bmo-doc-slot__remove` pins the button to that square in global.css; the two
 * numbers have to move together.
 * </p>
 */
const REMOVE_SLOT_BUTTON_PX = 32;

/**
 * The "another document already uses this name" line under a bucket row's description box.
 *
 * <p>
 * Lives beside the box rather than only firing as a toast on a refused drop, because the advisor
 * is typing here and a message that appears only when they later drag a file arrives too late to
 * be read as being about the text.
 * </p>
 *
 * <p>
 * Renders on a COMMITTED row as readily as on one still waiting for a file, and that is the whole
 * point of it. The description stays editable until the file reaches BMO (see `isNameLocked`), so
 * the two ways to end up with a clash are to type one and to type one AFTER uploading — and the
 * second is the likelier of the pair, since a row that already holds its file is the one the
 * advisor comes back to. A check that stopped at the pending row would be silent for exactly the
 * flow that produces the collision the server later refuses the whole batch for.
 * </p>
 *
 * <h3>Which message</h3>
 * A row whose description names something is told to describe it differently — that text is the
 * DOCNAME and changing it fixes the clash. A row whose description names NOTHING is delivered
 * under its file's own name instead, so it is told the opposite: give it a description, because
 * there is no other way to move it off the name the file lends it.
 *
 * <h3>When it says nothing</h3>
 * A row with no file and no usable description. The DOCNAME would fall back to a file that is not
 * there, so there is nothing to compare and guessing would flag rows that are perfectly fine. The
 * row is checked the moment its file lands, which is when the real name first exists.
 */
/**
 * Whether this row clashes, and under what name — the decision {@link NameDuplicateError} renders
 * and the one its row has to make BEFORE rendering, to know whether `aria-describedby` has
 * anything to point at.
 *
 * Split out rather than left inside the component so those two answers come from ONE place. A row
 * that decided describedby on its own would be a second copy of this rule, and the copies drift:
 * the failure mode is an `aria-describedby` naming an id that was never rendered, which is worse
 * than no reference at all — a screen reader silently reads nothing and the advisor is told less
 * than a sighted user, with no sign anything is missing.
 */
function duplicateNameClash(
  value: string,
  file: Pick<File, 'name'> | undefined,
  taken: ReadonlySet<string>,
): { described: boolean; shown: string } | null {
  // With a file there is always a name — the description, else the file's own, else the server's
  // SUPPORTINGDOC substitute — so `bucketDeliveryName` answers for every committed row. Without
  // one only the typed text can be judged, and `pendingDeliveryName` returns null when it names
  // nothing rather than inventing a comparison.
  const name = file ? bucketDeliveryName(value, file) : pendingDeliveryName(value);
  if (name === null || !taken.has(name)) return null;
  const described = describedDocumentName(value);
  // Both branches quote the UNFOLDED name, so the advisor is shown the name as they would
  // recognise it rather than as `VOID-CHEQUE`. The second is only ever reached with a file: a
  // fileless row whose box names nothing produced a null name above and has already returned.
  return {
    described: described !== '',
    shown: described || (file ? bucketDocumentName(value, file) : name),
  };
}

/**
 * Gap between the description box and the message under it, wide enough to clear AntD's
 * `showCount` counter.
 *
 * <p>
 * The counter is absolutely positioned at `bottom: -(fontSize × lineHeight)` — a full line BELOW
 * the box and OUT OF FLOW, so it reserves no height and anything following at an ordinary 4px gap
 * is drawn straight through it. That is what put the duplicate-name line across "0 / 99". 22px is
 * AntD's own number for that line (14 × 1.5715, and theme.ts keeps `fontSize: 14`), so clearing it
 * exactly and then adding back the 4px the message always had leaves the text starting just under
 * the counter rather than on top of it. The two move together: a changed base font size moves the
 * counter and has to move this.
 * </p>
 */
const DESCRIPTION_COUNT_CLEARANCE_PX = 22 + 4;

function NameDuplicateError({
  id,
  value,
  file,
  taken,
}: {
  /** Target of the description box's `aria-describedby`. The box only points here when this
   *  component actually renders — see {@link duplicateNameClash}. */
  id: string;
  value: string;
  /** The file this row holds. Absent on a row still waiting for one. */
  file?: Pick<File, 'name'>;
  taken: ReadonlySet<string>;
}) {
  const { t } = useTranslation();
  const clash = duplicateNameClash(value, file, taken);
  if (clash === null) return null;
  return (
    <div
      id={id}
      role="alert"
      style={{ fontSize: 12, color: '#dc2626', marginTop: DESCRIPTION_COUNT_CLEARANCE_PX }}
    >
      {clash.described
        ? t('formRenderer.additionalDocuments.descriptionDuplicate', { name: clash.shown })
        : t('formRenderer.additionalDocuments.descriptionDuplicateUnnamed', { name: clash.shown })}
    </div>
  );
}

/**
 * The "Description" caption above a bucket row's box.
 *
 * A real `<label>` now, not the `Typography.Text` span it was. The span sat directly above the
 * textarea and read as its label to a sighted advisor while naming nothing at all to anyone else:
 * the box had no `<label for>`, no `aria-label` and no `aria-labelledby`, so a screen reader
 * announced it as an unlabelled edit field and the placeholder was the only clue — and a
 * placeholder is not a label (it disappears the moment anything is typed, which is exactly when a
 * committed row shows one). The style is carried over verbatim; `<label>` is inline like the span
 * it replaces, so nothing moves.
 */
const DESCRIPTION_LABEL_STYLE = { fontSize: 13, color: '#475569' } as const;

/**
 * One committed bucket file — its description on the left, the file where its dropzone was.
 *
 * <p>
 * Its own component purely so it can hold a `useId` family. The rows are rendered from a `map`,
 * and ids built from the loop index would be reassigned the moment a file is removed from the
 * middle — pointing a surviving row's `aria-describedby` at the error of a row that is no longer
 * there. `useId` is per MOUNTED instance and follows the row through a reorder. Same rule
 * FieldRenderer follows for the same reason.
 * </p>
 */
function CommittedFileRow({
  file,
  onDescriptionChange,
  onRemove,
  taken,
}: {
  file: UploadedBucketFile;
  onDescriptionChange: (next: string) => void;
  onRemove: () => void;
  taken: ReadonlySet<string>;
}) {
  const { t } = useTranslation();
  const uid = useId();
  const descId = `${uid}desc`;
  const dupId = `${uid}dup`;
  const value = file.description ?? '';
  const nameLocked = isNameLocked(file);
  // Asked once, so the box's `aria-describedby` and the message below it can never disagree about
  // whether there is a message to point at.
  const clash = duplicateNameClash(value, file.file, taken);

  return (
    <li className="bmo-doc-slot">
      <div>
        <label htmlFor={descId} style={DESCRIPTION_LABEL_STYLE}>
          {t('formRenderer.additionalDocuments.descriptionLabel')}
        </label>
        {/* Closed once the upload starts — see isNameLocked. The tooltip is what turns a
            dead-looking box into an instruction: the way to re-describe is Remove and
            upload again. Wrapped in a span because AntD's Tooltip cannot bind to a
            disabled control's own events, so without one the explanation would be
            unreachable exactly when it is needed. */}
        <Tooltip title={nameLocked ? t('formRenderer.additionalDocuments.descriptionLocked') : ''}>
          <span>
            <Input.TextArea
              id={descId}
              // NAMES ITS FILE. Every row's visible caption is the word "Description", so without
              // this a step holding four files offers four boxes all called "Description" and
              // nothing says which file each one renames. The visible word stays the leading one
              // (2.5.3) and the filename only disambiguates.
              aria-label={t('formRenderer.additionalDocuments.descriptionForFile', {
                name: file.file.name,
              })}
              aria-describedby={clash ? dupId : undefined}
              aria-invalid={clash !== null}
              value={value}
              onChange={(e) => onDescriptionChange(e.target.value)}
              placeholder={t('formRenderer.additionalDocuments.descriptionPlaceholder')}
              maxLength={BUCKET_DOCUMENT_DESCRIPTION_MAX_LENGTH}
              showCount
              disabled={nameLocked}
              style={{ marginTop: 4 }}
              styles={{ textarea: DESCRIPTION_TEXTAREA_STYLE }}
            />
          </span>
        </Tooltip>
        {/* OUTSIDE the Tooltip, so the message is part of the row rather than part of
            the control's hover affordance. The file is passed because a committed row
            is delivered under its file's name when the box is empty — which is itself a
            clash worth reporting, and the one the advisor cannot see coming. */}
        <NameDuplicateError id={dupId} value={value} file={file.file} taken={taken} />
      </div>
      <div>
        <div className="bmo-doc-slot__label-spacer" aria-hidden="true">
          {t('formRenderer.additionalDocuments.descriptionLabel')}
        </div>
        <UploadedFileCard file={file} onRemove={onRemove} />
      </div>
    </li>
  );
}

/**
 * One bucket row still waiting for a file — a description box and its own drop target.
 *
 * Extracted for the same `useId` reason as {@link CommittedFileRow}, and named by POSITION rather
 * than by a filename because it has no file yet: "document 3" is the only honest way to tell this
 * box and this drop target apart from the ones above them.
 *
 * <p>
 * An `<li>` like {@link CommittedFileRow}, because the two are rows of the SAME list: a row waiting
 * for a file becomes a committed one in place when its file lands, so they interleave and share one
 * numbering.
 * </p>
 */
function PendingSlotRow({
  description,
  position,
  acceptedAttr,
  beforeUpload,
  onDescriptionChange,
  onRemove,
  showRemoveGutter,
  taken,
}: {
  description: string;
  /** 1-based place in the optional list, committed rows included. */
  position: number;
  acceptedAttr: string | undefined;
  beforeUpload: (file: RcFile) => boolean | string;
  onDescriptionChange: (next: string) => void;
  /** Absent on the anchor row, which may not be deleted — see anchorRowKey. */
  onRemove?: () => void;
  showRemoveGutter: boolean;
  taken: ReadonlySet<string>;
}) {
  const { t } = useTranslation();
  const uid = useId();
  const descId = `${uid}desc`;
  const dupId = `${uid}dup`;
  const clash = duplicateNameClash(description, undefined, taken);

  return (
    <li className="bmo-doc-slot">
      <div>
        <label htmlFor={descId} style={DESCRIPTION_LABEL_STYLE}>
          {t('formRenderer.additionalDocuments.descriptionLabel')}
        </label>
        {/* Free text of up to BUCKET_DOCUMENT_DESCRIPTION_MAX_LENGTH characters, delivered verbatim as
            `supportingDocs[].description`. Its opening words are ALSO folded into §14.1's
            DOCNAME (see bucketDocumentName), which is what the duplicate check below is
            about. No character is refused, though: the fold rewrites what it cannot carry,
            and the full text still reaches BMO exactly as typed. */}
        <Input.TextArea
          id={descId}
          aria-label={t('formRenderer.additionalDocuments.descriptionForDocument', {
            index: position,
          })}
          aria-describedby={clash ? dupId : undefined}
          aria-invalid={clash !== null}
          maxLength={BUCKET_DOCUMENT_DESCRIPTION_MAX_LENGTH}
          showCount
          value={description}
          onChange={(e) => onDescriptionChange(e.target.value)}
          placeholder={t('formRenderer.additionalDocuments.descriptionPlaceholder')}
          style={{ marginTop: 4 }}
          styles={{ textarea: DESCRIPTION_TEXTAREA_STYLE }}
        />
        <NameDuplicateError id={dupId} value={description} taken={taken} />
      </div>
      {/* The left column has a "Description" label above its input, so the
        drop target needs the same leading offset to line up with the box
        rather than the label. A hidden copy of the label reserves exactly
        that height, which survives any font or spacing change. */}
      <div>
        <div className="bmo-doc-slot__label-spacer" aria-hidden="true">
          {t('formRenderer.additionalDocuments.descriptionLabel')}
        </div>
        {/* Delete sits at the row's trailing edge, which is where the committed rows above
            carry their own Remove — the two read as one column of row controls rather than
            as two unrelated buttons. It is NOT inside the Dragger: anything rendered in
            there is part of the click target, so the button would open the file picker.
            Absent on the anchor row — see anchorRowKey. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <DropZone
              beforeUpload={beforeUpload}
              acceptedAttr={acceptedAttr}
              // No file yet, so position is the only honest way to tell this target from the ones
              // above it.
              slotNote={t('formRenderer.additionalDocuments.dropzoneFor', {
                name: t('formRenderer.additionalDocuments.documentN', { index: position }),
              })}
              t={t}
            />
          </div>
          {onRemove ? (
            <Tooltip title={t('formRenderer.additionalDocuments.removeRow')}>
              <Button
                className="bmo-doc-slot__remove"
                type="text"
                icon={<DeleteOutlined />}
                onClick={onRemove}
                aria-label={t('formRenderer.additionalDocuments.removeRowN', { index: position })}
              />
            </Tooltip>
          ) : (
            showRemoveGutter && (
              <div style={{ width: REMOVE_SLOT_BUTTON_PX, flexShrink: 0 }} aria-hidden="true" />
            )
          )}
        </div>
      </div>
    </li>
  );
}

/**
 * One row of the optional bucket as the advisor built it.
 *
 * <p>
 * `fileUid` is the committed file this row ended up holding — set when a file lands in the row's
 * own dropzone, and the reason an upload fills the row it was dropped on instead of adding one.
 * The file itself is NOT held here: it belongs to FormShell's store, which is what survives step
 * navigation, so the row only names it.
 * </p>
 */
type BucketSlot = { uid: string; description: string; fileUid?: string };

/** A row ready to render: the file it holds, or the description box still waiting for one. */
type BucketRow =
  | { kind: 'file'; key: string; file: UploadedBucketFile }
  | { kind: 'slot'; key: string; slot: BucketSlot };

/**
 * The unnamed "optional documents" bucket — a single dropzone that takes any
 * number of files of the originator-configured types, with no originator entry
 * behind them.
 *
 * <p>
 * The advisor DESCRIBES each file beside its dropzone. The full text travels as
 * `bmoData.supportingDocs[].description`, and its opening words become §14.1's DOCNAME — the one
 * field BMO tells one delivered document from another by (see bucketDocumentName). That second
 * role is why the box closes once the upload starts: the name is minted into the object's S3 key
 * and never rebuilt.
 * </p>
 */
function OptionalDocumentsBucket({
  allowedExtensions,
  maxFileSizeMb,
  combinedMaxSizeMb,
  maxDocuments,
  files,
  reservedDocumentNames,
  onFilesChange,
  onFileAdded,
}: {
  allowedExtensions: string[];
  maxFileSizeMb: number;
  /** Combined cap across the whole bucket. Null/absent = none authored. */
  combinedMaxSizeMb?: number | null;
  /** Originator-configured count cap, defaulted by the caller. */
  maxDocuments: number;
  files: UploadedBucketFile[];
  /** Folded DOCNAMEs the originator already reserved — see the caller for why they are not
   *  condition-filtered. */
  reservedDocumentNames: ReadonlySet<string>;
  onFilesChange: (
    next: UploadedBucketFile[] | ((prev: UploadedBucketFile[]) => UploadedBucketFile[]),
  ) => void;
  /**
   * Fired after a file passes validation and is committed locally.
   *
   * <p>
   * No DOCNAME travels with it any more. The name is composed at the deliver call from the
   * description as it stands THEN, so passing one here would be passing a guess that the advisor
   * is still free to change.
   * </p>
   */
  onFileAdded: (uid: string, file: File, indexInBucket: number) => void;
}) {
  const { t } = useTranslation();
  // The section's ROWS, in the order the advisor sees them — one "describe then upload" row per
  // the Figma, with ADD MORE appending another.
  //
  // A row holds only a description until a file lands in its own dropzone; at that point the file
  // is committed to the store and the row CLAIMS it by uid, so the same row turns into the
  // committed one in place. Claiming rather than appending is what keeps this one list: the two
  // used to be separate, so five rows and one upload read as six documents and the file appeared
  // somewhere other than the target it was dropped on.
  //
  // Local state because a row with no file yet isn't an uploaded document and has nothing to
  // persist — the files themselves live in FormShell's store, which is why a claim is by uid
  // rather than by holding the file here.
  // crypto.randomUUID() runs in the initializer, never during render.
  const [slots, setSlots] = useState<BucketSlot[]>(() => [
    { uid: crypto.randomUUID(), description: '' },
  ]);
  const setSlotDescription = (uid: string, description: string) =>
    setSlots((prev) => prev.map((sl) => (sl.uid === uid ? { ...sl, description } : sl)));
  /**
   * Drops one row that is still waiting for a file.
   *
   * <p>
   * Committed files are removed through their own card instead (`removeFile`): there is no separate
   * row to delete there, since dropping the file drops the row with it.
   * </p>
   */
  const removeSlot = (uid: string) => setSlots((prev) => prev.filter((sl) => sl.uid !== uid));

  // The section always offers somewhere to start. Every other row count is the advisor's own
  // doing — uploading consumes a row and ADD MORE puts one back — but removing the last file of
  // an otherwise empty section must not leave the heading with nothing under it.
  useEffect(() => {
    if (slots.length === 0 && files.length === 0) {
      setSlots([{ uid: crypto.randomUUID(), description: '' }]);
    }
  }, [slots.length, files.length]);

  const acceptedAttr = acceptAttr(allowedExtensions);
  const allowedListText = allowedExtensions.map((e) => e.toUpperCase()).join(', ');
  const maxBytes = maxFileSizeMb * 1024 * 1024;

  // How many files are committed, counting ones added earlier in the SAME drop. Picking several
  // files at once runs `beforeUpload` once per file before the step re-renders, so `files` is the
  // list as it stood when the drop began: counted from it, a twelve-file drop passed the cap
  // check twelve times, and each commit overwrote the last — one row survived, no limit message
  // showed, and the other eleven were still uploaded (QA D-44 / ESU-014). Resynced on every
  // render, so a removal frees its place again.
  const committedCount = useRef(files.length);
  useEffect(() => {
    committedCount.current = files.length;
  }, [files.length]);

  /**
   * The section as one list, in the order the advisor sees it: every row they built, each showing
   * either the file it claimed or the box still waiting for one.
   *
   * <p>
   * Files no row claims come FIRST. That is the store being read back — a Back into the step
   * remounts this component with fresh rows and a bucket full of files — and listing them above
   * the open rows is the order they were uploaded in.
   * </p>
   *
   * <p>
   * At the count cap the rows still waiting for a file drop out: there is nowhere left to put one,
   * so an open dropzone would only be refused.
   * </p>
   */
  const rows = useMemo<BucketRow[]>(() => {
    const byUid = new Map(files.map((f) => [f.uid, f]));
    const claimed = new Set<string>();
    const built: BucketRow[] = slots.map((slot) => {
      const file = slot.fileUid === undefined ? undefined : byUid.get(slot.fileUid);
      if (file === undefined) return { kind: 'slot', key: slot.uid, slot };
      claimed.add(file.uid);
      return { kind: 'file', key: file.uid, file };
    });
    const unclaimed: BucketRow[] = files
      .filter((f) => !claimed.has(f.uid))
      .map((f) => ({ kind: 'file', key: f.uid, file: f }));
    const all = [...unclaimed, ...built];
    return files.length >= maxDocuments ? all.filter((row) => row.kind === 'file') : all;
  }, [files, slots, maxDocuments]);

  /**
   * For each row, the names already spoken for when that row is reached: the originator's
   * reservations plus every row ABOVE it.
   *
   * <p>
   * Scoped to earlier siblings for two reasons. It is what keeps a row from being flagged against
   * its own name — a row compared against the whole section's names would report itself as a
   * duplicate of itself. And it reports a clash ONCE, on the lower of the two rows: flagging both
   * would leave the advisor choosing which of two identical messages to act on.
   * </p>
   */
  const takenByRow = useMemo(() => {
    const running = new Set(reservedDocumentNames);
    return rows.map((row) => {
      const upToHere = new Set(running);
      const name =
        row.kind === 'file'
          ? bucketDeliveryName(row.file.description, row.file.file)
          : pendingDeliveryName(row.slot.description);
      if (name !== null) running.add(name);
      return upToHere;
    });
  }, [reservedDocumentNames, rows]);

  // The combined cap across the bucket. Keyed on each file's own uid rather than on a slot,
  // because this section genuinely APPENDS — unlike a named entry, nothing here is replaced — so
  // every reservation is a new one and they accumulate.
  const budget = useSizeBudget(
    combinedMaxSizeMb,
    files.map((f) => ({ key: f.uid, bytes: f.file.size })),
  );

  /**
   * Hands the row that was dropped on the file it just took.
   *
   * <p>
   * The row keeps its place and gives up its description to the file, so an upload fills the row
   * the advisor aimed at rather than adding one below — and the count of rows still waiting for a
   * file goes DOWN by one, to a floor of none.
   * </p>
   *
   * <p>
   * A drop of several files runs the handler once per file before the step re-renders, so only the
   * first can claim that row. The rest take rows of their own immediately below it — after any
   * sibling the same drop has already placed, so what is on screen is the order they were picked.
   * </p>
   */
  const claimRowFor = (slotUid: string, fileUid: string) =>
    setSlots((prev) => {
      const at = prev.findIndex((sl) => sl.uid === slotUid);
      if (at < 0) return prev;
      if (prev[at]!.fileUid === undefined) {
        return prev.map((sl, i) => (i === at ? { ...sl, description: '', fileUid } : sl));
      }
      let insertAt = at + 1;
      while (insertAt < prev.length && prev[insertAt]!.fileUid !== undefined) insertAt++;
      const next = [...prev];
      // Keyed off the file's own uid, which is already unique per commit — `crypto.randomUUID()`
      // would make this updater impure, and React may call it more than once with the same input.
      next.splice(insertAt, 0, { uid: `row-${fileUid}`, description: '', fileUid });
      return next;
    });

  // Curried per slot so each dropzone commits its OWN description.
  const beforeUploadFor =
    (slotUid: string) =>
    (file: RcFile): boolean | string => {
      // NO NAME CHECK AT THE DROP ANY MORE. The §14.1 name is composed at the deliver call, so a
      // collision found here is not yet a fact about anything — the advisor can resolve it by
      // editing a description afterwards, which is exactly what the open box is for. Refusing
      // their file for it would be refusing a problem they have not had a chance to cause.
      //
      // Two things cover it instead: the inline message beside each description, which reports a
      // clash where it can still be fixed, and the server's own check at the deliver call, which
      // refuses the whole batch rather than letting one document silently overwrite another at
      // BMO. The second is the guarantee; the first is the one the advisor can act on.
      const typedNow = slots.find((sl) => sl.uid === slotUid)?.description ?? '';
      if (allowedExtensions.length > 0) {
        const dot = file.name.lastIndexOf('.');
        const ext = dot >= 0 ? file.name.slice(dot + 1).toLowerCase() : '';
        if (!allowedExtensions.includes(ext)) {
          void message.error(
            t('formRenderer.additionalDocuments.rejectedExtension', {
              name: file.name,
              allowed: allowedListText,
            }),
          );
          return Upload.LIST_IGNORE;
        }
      }
      if (file.size > maxBytes) {
        void message.error(
          t('formRenderer.additionalDocuments.rejectedSize', {
            name: file.name,
            max: maxFileSizeMb,
          }),
        );
        return Upload.LIST_IGNORE;
      }
      const indexInBucket = committedCount.current;
      if (indexInBucket >= maxDocuments) {
        void message.error(t('formRenderer.additionalDocuments.maxReached'));
        return Upload.LIST_IGNORE;
      }
      const uid = `${file.name}-${file.size}-${file.lastModified}-${indexInBucket}`;
      // Count first, then bytes: a file refused for one must not consume the other. Both are
      // checked before `committedCount` advances, so a refusal leaves the bucket untouched.
      if (!budget.tryReserve(uid, file.size)) {
        void message.error(
          t('formRenderer.additionalDocuments.rejectedCombinedSize', {
            name: file.name,
            max: budget.capMb,
          }),
        );
        return Upload.LIST_IGNORE;
      }
      committedCount.current = indexInBucket + 1;
      // Whatever the advisor typed beside THIS dropzone lands on the file as its description. It
      // stays editable from here until the file is delivered — the DOCNAME is composed from it
      // server-side at that point, not now.
      const typed = typedNow.trim();
      // The store stays in UPLOAD order, which is what `sequenceInPackage` is counted from and
      // what BMO reviews in. Where the file appears on screen is the row's business, below.
      onFilesChange((prev) => [...prev, { uid, file, description: typed }]);
      // Commit locally first, then upload — see the matching note in EntrySlot. No-ops in preview.
      onFileAdded(uid, file, indexInBucket);
      claimRowFor(slotUid, uid);
      return false;
    };

  const removeFile = (uid: string) => {
    onFilesChange(files.filter((f) => f.uid !== uid));
    // The row goes with the file it held. Removing the file IS how a committed row is deleted —
    // there is no separate row delete on one — so leaving the row behind would put an empty
    // dropzone where the advisor asked for nothing.
    setSlots((prev) => prev.filter((sl) => sl.fileUid !== uid));
  };

  // Every row the section holds, filled or not. ADD MORE stops at the cap, and a drop past it is
  // refused (QA D-44 / ESU-014: rows could be added without end).
  const atLimit = rows.length >= maxDocuments;

  /**
   * The first row still waiting for a file — the section's anchor, and the one row with no delete
   * button while nothing has been uploaded.
   *
   * <p>
   * On first load that row IS Optional Documents, and offering to delete it invites the advisor to
   * empty a section they have not used yet, leaving a heading with nothing under it and ADD MORE as
   * the only way back. ADD MORE and a deleted-then-restored row are not the same thing to read.
   * </p>
   *
   * <p>
   * Every other row is the advisor's own doing — a second ADD MORE click, or a row they decided
   * against — so those stay deletable, and once a file is committed the section has visible content
   * of its own and even this one may go. "I want none of these empty rows" stays a reachable end
   * state; "I want none of this section" never was one, since the heading is authored by the
   * originator.
   * </p>
   */
  const anchorRowKey = files.length > 0 ? null : rows.find((row) => row.kind === 'slot')?.key;
  // Reserve the delete column on every row as soon as ANY row carries the button, so the anchored
  // row's dropzone is the same width as the ones below it rather than 40px wider. A lone anchored
  // row has nothing to line up with, so it takes the full column — same rule as the repeatable
  // table's remove gutter.
  const showRemoveGutter = rows.some((row) => row.kind === 'slot' && row.key !== anchorRowKey);

  const setDescription = (uid: string, description: string) =>
    onFilesChange(files.map((f) => (f.uid === uid ? { ...f, description } : f)));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* ONE list, in the order the advisor built it: a row holds its description on the left and
          either the file it took or the target still waiting for one on the right. A file lands IN
          the row it was dropped on — these used to be two lists, which numbered five rows and one
          upload as six documents and put the file somewhere other than where it was aimed. */}
      {rows.length > 0 && (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 20 }}>
          {rows.map((row, index) =>
            row.kind === 'file' ? (
              <CommittedFileRow
                key={row.key}
                file={row.file}
                onDescriptionChange={(next) => setDescription(row.file.uid, next)}
                onRemove={() => removeFile(row.file.uid)}
                taken={takenByRow[index]!}
              />
            ) : (
              <PendingSlotRow
                key={row.key}
                description={row.slot.description}
                // Its place in the OPTIONAL list as the advisor sees it, filled rows included — the
                // number the accessible names below count by. Stable now that a committed row stays
                // where it was: an upload no longer renumbers the rows beneath it.
                position={index + 1}
                acceptedAttr={acceptedAttr}
                beforeUpload={beforeUploadFor(row.slot.uid)}
                onDescriptionChange={(next) => setSlotDescription(row.slot.uid, next)}
                onRemove={row.key === anchorRowKey ? undefined : () => removeSlot(row.slot.uid)}
                showRemoveGutter={showRemoveGutter}
                taken={takenByRow[index]!}
              />
            ),
          )}
        </ul>
      )}

      {atLimit ? (
        <Text style={{ fontSize: 13, color: '#475569' }}>
          {t('formRenderer.additionalDocuments.maxReached')}
        </Text>
      ) : (
        <Button
          type="link"
          /* `aria-hidden` because an @ant-design/icons glyph renders as
             `<span role="img" aria-label="plus-circle">`, and a button with no aria-label of its
             own takes its name from its CONTENTS — so this one announced as "plus-circle Add
             more". The glyph is decoration beside a button that already says what it does, and
             the raw icon name is the kind of thing that also breaks speech input: "click Add
             more" has to match the accessible name, not a prefix nobody can see. */
          icon={<PlusCircleOutlined style={{ fontSize: 18 }} aria-hidden="true" />}
          onClick={() =>
            setSlots((prev) => [...prev, { uid: crypto.randomUUID(), description: '' }])
          }
          style={{
            alignSelf: 'flex-start',
            paddingLeft: 0,
            height: 'auto',
            color: '#0079C1',
            fontWeight: 700,
            fontSize: 13,
            letterSpacing: 0.4,
            textTransform: 'uppercase',
          }}
        >
          {t('formRenderer.additionalDocuments.addMore')}
        </Button>
      )}
    </div>
  );
}
